import { bsConstants } from './module.constants'
import { newDate } from './utils'


/**
 * Legacy (flat) category seed list retained to support mapping old categories to the new taxonomy.
 * Do not delete without a migration plan.
 */
export const LegacySeedCategories = {
  ActionAndAdventure: {
    id: 1,
    name: 'Action & Adventure'
  },
  Erotica: {
    id: 2,
    name: 'Erotica'
  },
  Fantasy: {
    id: 3,
    name: 'Fantasy'
  },
  Horror: {
    id: 4,
    name: 'Horror'
  },
  Mystery: {
    id: 5,
    name: 'Mystery'
  },
  Nonfiction: {
    id: 6,
    name: 'Nonfiction'
  },
  OtherFiction: {
    id: 7,
    name: 'Other Fiction'
  },
  Romance: {
    id: 8,
    name: 'Romance'
  },
  ScienceFiction: {
    id: 9,
    name: 'Science Fiction'
  },
  Suspense: {
    id: 10,
    name: 'Suspense'
  },
  TeenAndYoungReaders: {
    id: 11,
    name: 'Teen and Young Readers'
  },
  Thriller: {
    id: 12,
    name: 'Thriller'
  },
  NoCategory: {
    id: 13,
    name: 'No Category'
  }
}

// as per story requirements
// https://app.shortcut.com/booksprout/story/24513/add-more-categories-back-to-booksprout
const LegacySeedCategoryIdsToNewSeedCategoryIds = {
  1: 7,
  2: 16,
  3: 25,
  4: 26,
  5: [4,5],
  6: 36,
  7: 19,
  8: 8,
  9: 24,
  10: [3,6,27],
  11: 28,
  12: [3,6]
}

/**
 * Temporary mapping for legacy (flat) category IDs (used by older clients)
 * to the new taxonomy's CHILD category IDs (i.e. selectable categories).
 *
 * Anything we can't reliably map falls back to "Nonfiction".
 */
export const LegacyToChildCategoryIds: Record<number, number[]> = {
  // Action & Adventure -> Mystery, Thrillers, Action (children)
  1: [2, 3, 4, 5, 6, 7],
  // Erotica -> Romance (children)
  2: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 58, 59, 66],
  // Fantasy -> Speculative Fiction (children)
  3: [24, 25, 26, 27, 60, 61, 62, 63],
  // Horror -> Speculative Fiction (children)
  4: [24, 25, 26, 27, 60, 61, 62, 63],
  // Mystery -> Mystery, Thrillers, Action (children)
  5: [2, 3, 4, 5, 6, 7],
  // Nonfiction -> Nonfiction (children)
  6: [37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47],
  // Other Fiction -> Fiction (children)
  7: [20, 21, 22],
  // Romance -> Romance (children)
  8: [9, 10, 11, 12, 13, 14, 15, 16, 17, 18, 58, 59, 66],
  // Science Fiction -> Speculative Fiction (children)
  9: [24, 25, 26, 27, 60, 61, 62, 63],
  // Suspense -> Mystery, Thrillers, Action (children)
  10: [2, 3, 4, 5, 6, 7],
  // Teen and Young Readers -> Teen & Young Readers (children)
  11: [29, 30, 31, 64],
  // Thriller -> Mystery, Thrillers, Action (children)
  12: [2, 3, 4, 5, 6, 7],
  // No Category -> fallback (Nonfiction children)
  13: [37, 38, 39, 40, 41, 42, 43, 44, 45, 46, 47]
}

export function mapLegacyCategoryToNewTopLevel (legacyCategoryId: number): number[] {
  const mapped = LegacyToChildCategoryIds[legacyCategoryId]
  // fallback for unknown / unmapped legacy IDs
  return mapped || LegacyToChildCategoryIds[LegacySeedCategories.Nonfiction.id]
}

export type SeedCategory = {
  id: number
  name: string
  parentId: number | null
}

/**
 * New hierarchical category seed list.
 * - `id` is a stable seed identifier (also matches DB id when seeding into a fresh empty DB in this order).
 * - `parentId` is the parent's seed id (or null for top-level categories).
 */
export const SeedCategories: Record<string, SeedCategory> = {
  // Mystery, Thrillers, Action
  MysteryThrillersAction: { id: 1, name: 'Mystery, Thrillers, Action', parentId: null },
  MysteryThrillersAction_CrimeFiction: { id: 2, name: 'Crime Fiction', parentId: 1 },
  MysteryThrillersAction_PsychologicalThrillers: { id: 3, name: 'Psychological Thrillers', parentId: 1 },
  MysteryThrillersAction_CozyMysteries: { id: 4, name: 'Cozy Mysteries', parentId: 1 },
  MysteryThrillersAction_HistoricalMysteries: { id: 5, name: 'Historical Mysteries', parentId: 1 },
  MysteryThrillersAction_Thrillers: { id: 6, name: 'Thrillers', parentId: 1 },
  MysteryThrillersAction_ActionAndAdventure: { id: 7, name: 'Action and Adventure', parentId: 1 },

  // Romance
  Romance: { id: 8, name: 'Romance', parentId: null },
  Romance_ContemporaryRomance: { id: 9, name: 'Contemporary Romance', parentId: 8 },
  Romance_RomCom: { id: 10, name: 'Rom-Com', parentId: 8 },
  Romance_HistoricalRomance: { id: 11, name: 'Historical Romance', parentId: 8 },
  Romance_RomanticSuspense: { id: 12, name: 'Romantic Suspense', parentId: 8 },
  Romance_YoungAdultNewAdultRomance: { id: 13, name: 'Young Adult / New Adult Romance', parentId: 8 },
  Romance_Romantasy: { id: 14, name: 'Romantasy', parentId: 8 },
  Romance_ParanormalRomance: { id: 15, name: 'Paranormal Romance', parentId: 8 },
  Romance_Erotica: { id: 16, name: 'Erotica', parentId: 8 },
  Romance_DarkRomance: { id: 17, name: 'Dark Romance', parentId: 8 },
  Romance_TimeTravelRomance: { id: 18, name: 'Time Travel Romance', parentId: 8 },
  Romance_SportsRomance: { id: 58, name: 'Sports Romance', parentId: 8 },
  Romance_SciFiRomance: { id: 59, name: 'Sci-Fi Romance', parentId: 8 },
  Romance_InspiredRomance: { id: 66, name: 'Inspired Romance', parentId: 8 },

  // Fiction
  Fiction: { id: 19, name: 'Fiction', parentId: null },
  Fiction_HistoricalFiction: { id: 20, name: 'Historical Fiction', parentId: 19 },
  Fiction_WomensFiction: { id: 21, name: "Women's Fiction", parentId: 19 },
  Fiction_LiteraryFiction: { id: 22, name: 'Literary Fiction', parentId: 19 },

  // Speculative Fiction
  SpeculativeFiction: { id: 23, name: 'Speculative Fiction', parentId: null },
  SpeculativeFiction_ScienceFiction: { id: 24, name: 'Science Fiction', parentId: 23 },
  SpeculativeFiction_Fantasy: { id: 25, name: 'Fantasy', parentId: 23 },
  SpeculativeFiction_Horror: { id: 26, name: 'Horror', parentId: 23 },
  SpeculativeFiction_SupernaturalSuspense: { id: 27, name: 'Supernatural Suspense', parentId: 23 },
  SpeculativeFiction_AlternateHistory: { id: 60, name: 'Alternate History', parentId: 23 },
  SpeculativeFiction_PostApocalyptic: { id: 61, name: 'Post-Apocalyptic', parentId: 23 },
  SpeculativeFiction_Dystopia: { id: 62, name: 'Dystopia', parentId: 23 },
  SpeculativeFiction_ParallelUniverse: { id: 63, name: 'Parallel Universe', parentId: 23 },

  // Teen & Young Readers
  TeenAndYoungReaders: { id: 28, name: 'Teen & Young Readers', parentId: null },
  TeenAndYoungReaders_TeenAndYoungAdult: { id: 29, name: 'Teen and Young Adult', parentId: 28 },
  TeenAndYoungReaders_ChildrensPictureBook: { id: 30, name: "Children's Picture Book", parentId: 28 },
  TeenAndYoungReaders_MiddleGrade: { id: 31, name: 'Middle Grade', parentId: 28 },
  TeenAndYoungReaders_ChildrensFiction: { id: 64, name: "Children's Fiction", parentId: 28 },

  // Religion & Spirituality
  ReligionAndSpirituality: { id: 32, name: 'Religion & Spirituality', parentId: null },
  ReligionAndSpirituality_ChristianNonfiction: { id: 33, name: 'Christian Nonfiction', parentId: 32 },
  ReligionAndSpirituality_ChristianFiction: { id: 34, name: 'Christian Fiction', parentId: 32 },
  ReligionAndSpirituality_ReligionAndSpirituality: { id: 35, name: 'Religion and Spirituality', parentId: 32 },

  // Nonfiction
  Nonfiction: { id: 36, name: 'Nonfiction', parentId: null },
  Nonfiction_AdviceAndHowTo: { id: 37, name: 'Advice and How-To', parentId: 36 },
  Nonfiction_BiographiesAndMemoirs: { id: 38, name: 'Biographies and Memoirs', parentId: 36 },
  Nonfiction_History: { id: 39, name: 'History', parentId: 36 },
  Nonfiction_GeneralNonfiction: { id: 40, name: 'General Nonfiction', parentId: 36 },
  Nonfiction_Science: { id: 41, name: 'Science', parentId: 36 },
  Nonfiction_PoliticsAndCurrentEvents: { id: 42, name: 'Politics and Current Events', parentId: 36 },
  Nonfiction_Cooking: { id: 43, name: 'Cooking', parentId: 36 },
  Nonfiction_Parenting: { id: 44, name: 'Parenting', parentId: 36 },
  Nonfiction_TrueCrime: { id: 45, name: 'True Crime', parentId: 36 },
  Nonfiction_Business: { id: 46, name: 'Business', parentId: 36 },
  Nonfiction_Humor: { id: 47, name: 'Humor', parentId: 36 },

  // LGBTQ+
  LGBTQPlus: { id: 48, name: 'LGBTQ+', parentId: null },
  LGBTQPlus_GayMM: { id: 49, name: 'Gay (MM)', parentId: 48 },
  LGBTQPlus_LesbianFF: { id: 50, name: 'Lesbian (FF)', parentId: 48 },
  LGBTQPlus_TransAndNonbinary: { id: 51, name: 'Trans and Nonbinary', parentId: 48 },
  LGBTQPlus_QueerFiction: { id: 52, name: 'Queer Fiction', parentId: 48 },

  // Diverse Voices
  DiverseVoices: { id: 53, name: 'Diverse Voices', parentId: null },
  DiverseVoices_BlackAfricanDiaspora: { id: 54, name: 'Black / African Diaspora', parentId: 53 },
  DiverseVoices_AsianPacificIslander: { id: 55, name: 'Asian / Pacific Islander', parentId: 53 },
  DiverseVoices_LatinxHispanic: { id: 56, name: 'Latinx / Hispanic', parentId: 53 },
  DiverseVoices_Indigenous: { id: 57, name: 'Indigenous', parentId: 53 },
  DiverseVoices_MiddleEasternNorthAfrican: { id: 65, name: 'Middle Eastern / North African', parentId: 53 }
}


const baseNotification = {
  active: true,
  typeFlags: bsConstants.NOTIFICATIONS.FLAGS.EMAIL | bsConstants.NOTIFICATIONS.FLAGS.APP | bsConstants.NOTIFICATIONS.FLAGS.MOBILE_APP
}

export const SeedNotifications = {
  NewArc: {
    ...baseNotification,
    id: 1,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.NEW_ARC.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.NEW_ARC.TEMPLATE_ID,
    permissionPreferenceKey: 'alertNewArc'
  },
  TimeToPublish: {
    ...baseNotification,
    id: 2,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.TIME_TO_PUBLISH.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.TIME_TO_PUBLISH.TEMPLATE_ID,
    permissionPreferenceKey: 'alertTimeToPublish'
  },
  ReviewsDueSoon: {
    ...baseNotification,
    id: 3,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.REVIEW_DUE_SOON.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.REVIEW_DUE_SOON.TEMPLATE_ID,
    permissionPreferenceKey: 'alertReviewDueIn2Days'
  },
  readyToReviewEverywhere: {
    ...baseNotification,
    id: 4,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.READY_TO_REVIEW.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.READY_TO_REVIEW.TEMPLATE_ID,
    permissionPreferenceKey: 'alertReadyToReview'
  },
  finalFollowUp: {
    ...baseNotification,
    id: 5,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.FINAL_FOLLOW_UP.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.FINAL_FOLLOW_UP.TEMPLATE_ID,
    permissionPreferenceKey: 'alertFinalFollowUp'
  },
  delayExpiring: {
    ...baseNotification,
    id: 6,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.DELAY_EXPIRING.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.DELAY_EXPIRING.TEMPLATE_ID,
    permissionPreferenceKey: 'alertDelayExpiring'
  },
  delayExpired: {
    ...baseNotification,
    id: 7,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.DELAY_EXPIRED.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.DELAY_EXPIRED.TEMPLATE_ID,
    permissionPreferenceKey: 'alertDelayExpired'
  },
  newArcParticipant: {
    ...baseNotification,
    id: 8,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.NEW_ARC_PARTICIPANT.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.NEW_ARC_PARTICIPANT.TEMPLATE_ID,
    permissionPreferenceKey: 'newArcParticipant'
  },
  newReview: {
    ...baseNotification,
    id: 9,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.NEW_REVIEW.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.NEW_REVIEW.TEMPLATE_ID,
    permissionPreferenceKey: 'newReview'
  },
  arcCancellation: {
    ...baseNotification,
    id: 10,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.ARC_CANCELLATION.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.ARC_CANCELLATION.TEMPLATE_ID,
    permissionPreferenceKey: 'arcCancellation'
  },
  reviewRejected: {
    ...baseNotification,
    id: 11,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.REVIEW_REJECTED.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.REVIEW_REJECTED.TEMPLATE_ID,
    permissionPreferenceKey: 'reviewRejected'
  },
  newExternalReview: {
    ...baseNotification,
    id: 12,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.NEW_EXTERNAL_REVIEW.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.NEW_EXTERNAL_REVIEW.TEMPLATE_ID,
    permissionPreferenceKey: 'newExternalReview'
  },
  freeText: {
    ...baseNotification,
    id: 13,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.FREE_TEXT.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.FREE_TEXT.TEMPLATE_ID,
    permissionPreferenceKey: 'freeText'
  },
  invitedToTeam: {
    ...baseNotification,
    id: 14,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.INVITED_TO_TEAM.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.INVITED_TO_TEAM.TEMPLATE_ID,
    permissionPreferenceKey: 'invitedToTeam'
  },
  arcCannotActivate: {
    ...baseNotification,
    // No email: its template was never created in Postmark [sc-29038]
    typeFlags: bsConstants.NOTIFICATIONS.FLAGS.APP | bsConstants.NOTIFICATIONS.FLAGS.MOBILE_APP,
    id: 15,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.ARC_CANNOT_ACTIVATE.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.ARC_CANNOT_ACTIVATE.TEMPLATE_ID,
    permissionPreferenceKey: 'arcCannotActivate'
  },
  bookFileUpdated: {
    ...baseNotification,
    id: 17,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.BOOK_FILE_UPDATED.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.BOOK_FILE_UPDATED.TEMPLATE_ID,
    permissionPreferenceKey: 'alertBookFileUpdated'
  },
  savedArcEndingSoon: {
    ...baseNotification,
    id: 18,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.SAVED_ARC_ENDING_SOON.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.SAVED_ARC_ENDING_SOON.TEMPLATE_ID,
    permissionPreferenceKey: 'alertSavedArcEndingOrLowCopies'
  },
  savedArcLowCopies: {
    ...baseNotification,
    id: 19,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.SAVED_ARC_LOW_COPIES.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.SAVED_ARC_LOW_COPIES.TEMPLATE_ID,
    permissionPreferenceKey: 'alertSavedArcEndingOrLowCopies'
  },
  arcDigest: {
    ...baseNotification,
    // Digest is email-only.
    typeFlags: bsConstants.NOTIFICATIONS.FLAGS.EMAIL,
    id: 20,
    code: bsConstants.NOTIFICATIONS.NOTIFIERS.ARC_DIGEST.CODE,
    lastQueuedDate: newDate(0),
    templateId: bsConstants.NOTIFICATIONS.NOTIFIERS.ARC_DIGEST.TEMPLATE_ID,
    permissionPreferenceKey: 'alertNewArcDigest'
  }
}
